import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Header from "../components/Header";
import { api, formatDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useT } from "../i18n";
import { ArrowLeft, CalendarDays, AlertCircle } from "lucide-react";

const CAT_CLS = {
  iva: "bg-[#EEF2FF] text-[#3730A3] border-[#C7D2FE]",
  ss: "bg-[#FEF3C7] text-[#92400E] border-[#FDE68A]",
  retencoes: "bg-[#E0F2FE] text-[#075985] border-[#BAE6FD]",
  irc: "bg-[#F3E8FF] text-[#6B21A8] border-[#E9D5FF]",
  outros: "bg-[color:var(--paper)] text-[color:var(--ink-muted)] border-[color:var(--border)]",
};

export default function FiscalCalendar() {
  const { user } = useAuth();
  const { t } = useT();
  const isAccountant = user?.role === "accountant";
  const [companies, setCompanies] = useState([]);
  const [companyId, setCompanyId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [savingRegime, setSavingRegime] = useState(false);

  useEffect(() => {
    if (!isAccountant) return;
    (async () => {
      const { data: comps } = await api.get("/companies");
      setCompanies(comps);
      if (comps.length) setCompanyId(comps[0].id);
      else setLoading(false);
    })();
  }, [isAccountant]);

  const fetchCalendar = useCallback(async () => {
    setLoading(true);
    try {
      const params = isAccountant ? { company_id: companyId } : {};
      if (isAccountant && !companyId) { setData(null); return; }
      const { data: res } = await api.get("/fiscal-calendar", { params });
      setData(res);
    } finally { setLoading(false); }
  }, [isAccountant, companyId]);

  useEffect(() => {
    if (isAccountant && !companyId) return;
    fetchCalendar();
  }, [fetchCalendar, isAccountant, companyId]);

  const changeRegime = async (regime) => {
    setSavingRegime(true);
    try {
      await api.patch(`/companies/${companyId}/vat-regime`, { vat_regime: regime });
      await fetchCalendar();
    } finally { setSavingRegime(false); }
  };

  const todayIso = new Date().toISOString().slice(0, 10);
  const events = useMemo(() => data?.events || [], [data]);
  const upcoming = useMemo(() => events.filter((e) => e.date >= todayIso).slice(0, 5), [events, todayIso]);

  const grouped = useMemo(() => {
    const map = {};
    events.forEach((e) => {
      const m = e.date.slice(0, 7);
      (map[m] = map[m] || []).push(e);
    });
    return Object.entries(map);
  }, [events]);

  const monthLabel = (ym) => {
    const [y, m] = ym.split("-");
    return `${new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, { month: "long" })} ${y}`;
  };
  const dayNum = (iso) => iso.slice(8, 10);

  return (
    <div className="paper-grain min-h-screen pb-16">
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="fiscal-calendar-page">
        <Link to={isAccountant ? "/contabilista" : "/cliente"} className="inline-flex items-center gap-2 text-sm text-[color:var(--ink-muted)] hover:text-[color:var(--ink)]">
          <ArrowLeft size={16} /> {t("common.back")}
        </Link>

        <div className="mt-4 flex items-end justify-between flex-wrap gap-3">
          <div>
            <span className="label-eyebrow">{t("fc.eyebrow")}</span>
            <h1 className="font-display text-3xl sm:text-4xl mt-1 flex items-center gap-3">
              <CalendarDays className="text-[color:var(--primary)]" size={28} /> {t("fc.title")}
            </h1>
          </div>
        </div>

        {/* Controls */}
        <div className="card-soft mt-6 flex flex-wrap items-end gap-4" data-testid="calendar-controls">
          {isAccountant && (
            <div>
              <label className="label-eyebrow block mb-1">{t("fc.company")}</label>
              <select className="input-soft py-2 text-sm min-w-[220px]" value={companyId} onChange={(e) => setCompanyId(e.target.value)} data-testid="calendar-company-filter">
                {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
          )}
          <div>
            <label className="label-eyebrow block mb-1">{t("fc.regime")}</label>
            {isAccountant ? (
              <select className="input-soft py-2 text-sm min-w-[150px]" value={data?.vat_regime || "monthly"} disabled={savingRegime || !companyId} onChange={(e) => changeRegime(e.target.value)} data-testid="vat-regime-select">
                <option value="monthly">{t("fc.monthly")}</option>
                <option value="quarterly">{t("fc.quarterly")}</option>
              </select>
            ) : (
              <div className="text-sm font-medium mt-1" data-testid="vat-regime-label">{data?.vat_regime === "quarterly" ? t("fc.quarterly") : t("fc.monthly")}</div>
            )}
          </div>
          {data?.year && <div className="text-sm text-[color:var(--ink-muted)] ml-auto">{data.year}</div>}
        </div>

        <p className="text-xs text-[color:var(--ink-muted)] flex items-start gap-2 mt-3">
          <AlertCircle size={14} className="mt-0.5 shrink-0" /> {t("fc.disclaimer")}
        </p>

        {loading ? (
          <div className="text-[color:var(--ink-muted)] mt-8">{t("common.loading")}</div>
        ) : !data ? (
          <div className="card-soft text-center py-12 mt-6">{t("fc.empty")}</div>
        ) : (
          <>
            {/* Upcoming */}
            {upcoming.length > 0 && (
              <div className="card-soft mt-6" data-testid="upcoming-card">
                <span className="label-eyebrow">{t("fc.upcoming")}</span>
                <ul className="mt-3 space-y-2">
                  {upcoming.map((e) => (
                    <li key={`${e.date}-${e.title}`} className="flex items-center gap-3 text-sm">
                      <span className="font-mono-data text-[color:var(--primary)] w-20 shrink-0">{formatDate(e.date)}</span>
                      <span className="flex-1">{e.title}</span>
                      <span className={`chip border ${CAT_CLS[e.category] || CAT_CLS.outros}`}>{e.category.toUpperCase()}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Full year grouped by month */}
            <div className="mt-6 space-y-5">
              {grouped.map(([ym, evs]) => (
                <div key={ym} className="card-soft" data-testid={`month-${ym}`}>
                  <h2 className="font-display text-lg capitalize">{monthLabel(ym)}</h2>
                  <ul className="mt-3 divide-y divide-[color:var(--border)]">
                    {evs.map((e) => (
                      <li key={`${e.date}-${e.title}`} className="flex items-start gap-4 py-3">
                        <div className="text-center w-10 shrink-0">
                          <div className="font-display text-xl leading-none">{dayNum(e.date)}</div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium">{e.title}</span>
                            <span className={`chip border text-xs ${CAT_CLS[e.category] || CAT_CLS.outros}`}>{e.category.toUpperCase()}</span>
                          </div>
                          <p className="text-sm text-[color:var(--ink-muted)] mt-0.5">{e.description}</p>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
